import { useCallback } from 'react';
import { useInboxFiltersShallowStore } from '../stores/inbox-store';
import {
  InboxFilters,
  InboxStatus,
  InboxTabType,
  InboxTabTypes,
  InboxTaskStatus,
  AIReceptionistFilterStatus,
} from '../types';

const VALID_STATUSES = new Set<string>(Object.values(InboxStatus));
const VALID_TASK_STATUSES = new Set<string>(Object.values(InboxTaskStatus));
const VALID_AI_RECEPTIONIST_STATUSES = new Set<string>(Object.values(AIReceptionistFilterStatus));
const VALID_TABS = new Set<string>(Object.values(InboxTabType));

const ARRAY_FILTER_KEYS = ['statuses', 'tags', 'locationPhones', 'tasks', 'aiReceptionist'] as const;
const STRING_FILTER_KEYS = ['startDate', 'endDate', 'startTime', 'endTime'] as const;

type ArrayFilterKey = (typeof ARRAY_FILTER_KEYS)[number];

const ENUM_VALIDATORS: Partial<Record<ArrayFilterKey, Set<string>>> = {
  statuses: VALID_STATUSES,
  tasks: VALID_TASK_STATUSES,
  aiReceptionist: VALID_AI_RECEPTIONIST_STATUSES,
};

/** Result returned by filter and tab mutation methods. */
export type SetInboxFiltersResult = { success: true; errors: string[] } | { success: false; errors: string[] };

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Validates and sanitizes a single array filter field.
 * Strips non-string items, empty strings, invalid enum values, and duplicates.
 *
 * @param key - The array filter key to sanitize (e.g. `'statuses'`, `'tags'`).
 * @param value - The raw value to sanitize.
 * @returns A tuple of [sanitized array, list of error messages]. Returns `undefined` for the
 *          array if the input is not an array at all.
 */
function sanitizeArrayField(
  key: ArrayFilterKey,
  value: InboxFilters[ArrayFilterKey]
): [string[] | undefined, string[]] {
  const errors: string[] = [];

  if (!Array.isArray(value)) {
    errors.push(`"${key}" must be an array, received ${typeof value}`);
    return [undefined, errors];
  }

  const filtered: string[] = [];
  for (const item of value) {
    if (!isNonEmptyString(item)) {
      errors.push(`"${key}" contains a non-string or empty item: ${JSON.stringify(item)}`);
    } else {
      filtered.push(item);
    }
  }

  const allowedValues = ENUM_VALIDATORS[key];
  const validated: string[] = [];
  if (allowedValues) {
    for (const v of filtered) {
      if (allowedValues.has(v)) {
        validated.push(v);
      } else {
        errors.push(`"${key}" contains invalid enum value: "${v}"`);
      }
    }
  } else {
    validated.push(...filtered);
  }

  return [[...new Set(validated)], errors];
}

/**
 * Validates a single string filter field.
 *
 * @param key - The string filter key (e.g. `'startDate'`).
 * @param value - The raw value to validate.
 * @returns A tuple of [sanitized string, list of error messages]. Returns `undefined` for the
 *          string if the input is not a string.
 */
function sanitizeStringField(key: string, value: string): [string | undefined, string[]] {
  if (typeof value === 'string') return [value, []];
  return [undefined, [`"${key}" must be a string, received ${typeof value}`]];
}

/**
 * Sanitizes a partial inbox filters object. Validates each known field and strips any
 * values that don't match the expected types or allowed enum members.
 *
 * Unknown keys are ignored. Fields that fail validation are excluded from the result
 * and reported in the `errors` array.
 *
 * @param input - A partial `InboxFilters` object to sanitize.
 * @returns An object containing the sanitized `filters` (or `null` if nothing was valid) and
 *          an array of human-readable `errors` describing what was rejected.
 *
 * @example
 * ```ts
 * const { filters, errors } = sanitizeInboxFilters({
 *   statuses: [InboxStatus.UNREAD, 'bogus'],
 *   startDate: 42,
 * });
 * // filters → { statuses: ['new'] }
 * // errors → ['"statuses" contains invalid enum value: "bogus"', '"startDate" must be a string, received number']
 * ```
 */
export function sanitizeInboxFilters(input: Partial<InboxFilters>): {
  filters: Partial<InboxFilters> | null;
  errors: string[];
} {
  if (input == null || typeof input !== 'object') {
    return { filters: null, errors: ['Input must be a non-null object'] };
  }

  const raw = input as Record<string, InboxFilters[keyof InboxFilters]>;
  const result: Partial<InboxFilters> = {};
  const allErrors: string[] = [];
  let hasField = false;

  for (const key of ARRAY_FILTER_KEYS) {
    if (key in raw) {
      const [sanitized, errors] = sanitizeArrayField(key, raw[key] as InboxFilters[ArrayFilterKey]);
      allErrors.push(...errors);
      if (sanitized !== undefined) {
        (result as Record<string, InboxFilters[keyof InboxFilters]>)[key] = sanitized;
        hasField = true;
      }
    }
  }

  for (const key of STRING_FILTER_KEYS) {
    if (key in raw) {
      const [sanitized, errors] = sanitizeStringField(key, raw[key] as string);
      allErrors.push(...errors);
      if (sanitized !== undefined) {
        result[key] = sanitized;
        hasField = true;
      }
    }
  }

  return { filters: hasField ? result : null, errors: allErrors };
}

/**
 * Type guard that checks whether a value is a valid `InboxTabTypes` enum member.
 *
 * @param value - The value to check.
 * @returns `true` if the value is `'actionable'` or `'all'`.
 */
export function isValidInboxTab(value: InboxTabTypes): value is InboxTabTypes {
  return typeof value === 'string' && VALID_TABS.has(value);
}

/**
 * Wrapper hook over the messaging inbox filters store.
 *
 * Provides a safe abstraction layer with runtime validation, sanitization, and error
 * reporting. All filter mutations go through guards that reject invalid types, unknown
 * enum values, empty strings in arrays, and duplicates — preventing bad data from
 * reaching the store.
 *
 * @returns An object with three methods:
 *  - `setFilters` — Accepts a `Partial<InboxFilters>`, sanitizes each field, updates the
 *     store with only the valid fields, and returns a `{ success, errors }` result.
 *  - `clearFilters` — Resets all filters to their default values.
 *  - `setTab` — Accepts an `InboxTabTypes` value, validates it, updates the store, and
 *     returns a `{ success, errors }` result.
 *
 * @example
 * ```tsx
 * const { setFilters, clearFilters, setTab } = useSetInboxFilters();
 *
 * const { success, errors } = setFilters({
 *   statuses: [InboxStatus.UNREAD],
 *   tags: ['tag-1'],
 * });
 *
 * if (!success) {
 *   console.warn('Some filters were rejected:', errors);
 * }
 * ```
 */
export function useSetInboxFilters() {
  const { setInboxFilters, clearInboxFilters, setInboxTab } = useInboxFiltersShallowStore(
    'setInboxFilters',
    'clearInboxFilters',
    'setInboxTab'
  );

  /**
   * Validates and applies a partial set of inbox filters to the store.
   *
   * Fields with invalid types or enum values are silently excluded from the store update
   * but reported in the returned `errors` array. If no valid fields remain after
   * sanitization, the store is not touched and `success` is `false`.
   *
   * @param filters - A partial `InboxFilters` object. Only known keys are processed.
   * @returns `{ success: true, errors }` if at least one field was applied, or
   *          `{ success: false, errors }` if nothing could be applied.
   */
  const setFilters = useCallback(
    (filters: Partial<InboxFilters>): SetInboxFiltersResult => {
      const { filters: sanitized, errors } = sanitizeInboxFilters(filters);
      if (sanitized) {
        setInboxFilters(sanitized as InboxFilters);
        return { success: true, errors };
      }
      return { success: false, errors };
    },
    [setInboxFilters]
  );

  /**
   * Resets all inbox filters back to their default values.
   */
  const clearFilters = useCallback(() => {
    clearInboxFilters();
  }, [clearInboxFilters]);

  /**
   * Validates and sets the active inbox tab.
   *
   * @param tab - The tab to activate. Must be a valid `InboxTabType` enum member.
   * @returns `{ success: true, errors: [] }` if valid, or
   *          `{ success: false, errors }` with the rejection reason.
   */
  const setTab = useCallback(
    (tab: InboxTabTypes): SetInboxFiltersResult => {
      if (isValidInboxTab(tab)) {
        setInboxTab(tab);
        return { success: true, errors: [] };
      }
      return {
        success: false,
        errors: [`Invalid tab value: "${String(tab)}". Expected one of: ${[...VALID_TABS].join(', ')}`],
      };
    },
    [setInboxTab]
  );

  return { setFilters, clearFilters, setTab } as const;
}
